/**
 * Sales Negotiation: silence-stage timing, as a pure function.
 *
 * A character's silenceProfile lists stages such as
 *   [{ type: 'warn', delay: 8000 }, { type: 'warn', delay: 15000 }, { type: 'hangup', delay: 22000 }]
 * whose delays are TOTAL silence: warn at 8s, warn again at 15s, hang up at 22s.
 *
 * VoiceRoom's silence clock restarts every time the prospect finishes speaking, so a
 * later stage cannot simply wait for its full `delay` after the previous line (that
 * made the second warning arrive after 15s MORE silence, i.e. 23s+ in all). Instead
 * it waits for `delay` minus the delay of the last stage that actually spoke.
 *
 * No imports and no browser APIs, so it loads in the browser and in Node tests.
 */

/**
 * How long the silence clock must run, counted from the last restart, before
 * `stages[index]` fires.
 *
 * @param stages            the character's silenceProfile.stages
 * @param index             the stage about to fire
 * @param lastSpokenIndex   the latest stage whose line was actually spoken since the rep last
 *                          spoke (-1 if none). Speaking a line restarts the clock; a stage with
 *                          no line text does not, so the wait is still measured from the last
 *                          stage that did.
 * @returns milliseconds, or null if there is no such stage
 *
 * The first stage, and any stage after a restart by the rep, waits its full delay, so a
 * single-stage character behaves exactly as before. If the data is not increasing
 * (a later delay no bigger than the earlier one) the stage waits its own full delay
 * rather than firing instantly.
 */
export const stageWaitMs = (stages, index, lastSpokenIndex = -1) => {
  const stage = stages?.[index];
  if (!stage) return null;
  const base = lastSpokenIndex >= 0 && lastSpokenIndex < index ? stages[lastSpokenIndex].delay : 0;
  const wait = stage.delay - base;
  return wait > 0 ? wait : stage.delay;
};
